import type { ReactElement, ReactNode } from 'react';
import { Panel, Skeleton } from '@/components/ui';
import { cx } from '@/lib/cx';
import { formatDecimalString } from '@/lib/format';
import { compactMoneyParts } from '@/lib/targets';
import { CompactFigure, TILE_FIELD_LABEL } from '@/components/dashboards/TargetTiles';
import { shareOf, shareText, type ProductGroup } from './revenue-product';

/**
 * THE FOUR HEADLINE FIGURES — how much, from how many products, how
 * concentrated, and **how much of it is not attributed to a product at all**.
 *
 * ⚠️ **THE VISUAL IDIOM IS `AccountsStatCards`' AND `RevenueHeadline`'s,
 * DELIBERATELY**: one hairline grid (`gap-px` over `bg-border`, cells painted
 * `bg-surface`), the same imported `TILE_FIELD_LABEL`, the same `text-3xl`
 * figure. `TILE_FIELD_LABEL` is IMPORTED rather than restated so a fourth
 * dashboard cannot drift on a label style.
 *
 * ⚠️ **READ-OUTS, NOT CONTROLS.** Nothing here is clickable, so these are
 * `<div>`/`<p>` rather than `<button>`/`<span>`. The cycle strip below is where
 * this tab puts its clickable summary.
 *
 * ── 🔴 WHY THE FOURTH TILE EXISTS, AND WHY IT IS NOT OPTIONAL ──────────────
 *
 * On live data the uncatalogued bucket **dominates the older cycles** — whole
 * historical years for some companies sit almost entirely under it. A "best
 * sellers" ranking read without that in view is a ranking of whatever happened
 * to carry a catalogue item code, presented as a ranking of what sold. The tile
 * puts the proportion beside the ranking so the two are always read together.
 *
 * ⚠️ **IT REPORTS ON WHAT IS ON SCREEN, AND SWITCHES WHEN THE BUCKET IS
 * EXCLUDED.** With the bucket listed it is a share of the total beside it; with
 * the bucket filtered out that share is `0` by construction and would read as
 * *"almost everything is catalogued"* — the opposite of the truth. So it names
 * the excluded amount instead. One denominator, never two.
 *
 * ── 🔴 TILE 3 IS THE LARGEST **CATALOGUED** PRODUCT, AND THAT IS WHAT STOPS IT
 *    PRINTING TILE 4'S NUMBER ──────────────────────────────────────────────
 *
 * ⚠️ *SUPERSEDED: tile 3 read `groups[0]`, the largest row of any kind.*
 * Measured on screen 2026-09-11: **"Largest product" read 60.4% over "Imported
 * from Xero — no catalogue item" while "Uncatalogued" read 60.4% beside it** —
 * the same figure twice, because the bucket happened to top the ranking. Two
 * tiles carrying one number is a quarter of the headline row saying nothing.
 *
 * **It is fixed STRUCTURALLY rather than by merging the two when they
 * coincide**, for two reasons. The bucket is by definition *not a product*
 * (`UNCODED_BADGE` says so on every row of the table), so naming it under
 * "largest product" was already the wrong answer regardless of the collision.
 * And a conditional merge would leave an EMPTY CELL in a `gap-px` grid, which
 * the note at the foot of this block says reads as a tile that failed.
 *
 * So the two now answer different QUESTIONS in every state, never merely
 * different numbers:
 *
 *     tile 3   of the money attributed to a product, which product leads
 *     tile 4   how much of the scope is attributed to no product at all
 *
 * ⚠️ **THE DENOMINATOR IS STILL `total`, NOT THE CATALOGUED SUBTOTAL.** The
 * share column in the table below divides by the same figure, and a headline
 * that does not equal the shares under it reads as a broken page. So with the
 * bucket listed, tile 3 is *"this product is 12% of everything in scope"* and
 * will not be the table's top row — which is exactly what tile 4 beside it
 * explains.
 *
 * ⚠️ **NOTHING IS LOST BY NO LONGER NAMING THE BUCKET HERE.** *"The largest
 * thing is the bucket"* was strictly less information than tile 4's *"the
 * bucket is 60.4%"*, and the table still ranks it first with its badge.
 *
 * ── ⚠️ WHAT THESE FOUR FIGURES ARE ALLOWED TO CLAIM ────────────────────────
 *
 * Every one is **one currency, one cycle scope, paid invoices, incl. tax**
 * (owner 2026-10-16; *SUPERSEDED: ex-tax* — the label follows `taxLabel`), and
 * none of them is "revenue" without those four qualifiers — which is why the
 * currency code renders against the money rather than being left to the control
 * that set it. A figure read out of context, screenshotted or pasted, has to
 * carry its own unit.
 *
 * ⚠️ **A MISSING FIGURE IS AN EM DASH AND NEVER A ZERO.** "No revenue in this
 * currency" and "revenue of exactly zero" are different facts.
 *
 * ⛔ **FOUR TILES, AND THE BREAKPOINTS DIVIDE FOUR EXACTLY.** The grid is
 * `gap-px` over `bg-border`, so an EMPTY CELL is a visible break in the frame
 * rather than whitespace — it reads as a tile that failed to render. `1 → 2 → 4`
 * is exact at four; a fifth means re-cutting the counts.
 */

/** The headline figure style, shared so four tiles cannot drift. */
const FIGURE = 'tabular block text-2xl font-semibold text-fg';

/**
 * What a tile shows instead of a number.
 *
 * The `note` NAMES the state rather than explaining it — "No USD revenue" is a
 * measurement, which is the exception the no-helper-text rule already makes for
 * a fact the reader cannot otherwise see. A bare dash reads as a failed read.
 */
function NoFigure({ note }: { note: string }): ReactElement {
  return (
    <>
      <span className={cx(FIGURE, 'text-fg-subtle')}>—</span>
      <span className="mt-0.5 block text-xs text-fg-subtle">{note}</span>
    </>
  );
}

/** One cell of the hairline grid. */
function Tile({
  label,
  loading,
  children,
}: {
  label: string;
  loading: boolean;
  children: ReactNode;
}): ReactElement {
  return (
    <li className="flex min-w-0">
      <div className="flex min-w-0 flex-1 flex-col bg-surface px-4 py-4">
        <p className={cx('m-0', TILE_FIELD_LABEL)}>{label}</p>
        {/*
         * `mt-auto` bottom-aligns the figures across the row, so a label that
         * wraps at one width cannot leave one number a line below its neighbours.
         */}
        <div className="mt-auto block pt-2">
          {loading ? <Skeleton className="h-9 w-24" /> : children}
        </div>
      </div>
    </li>
  );
}

/**
 * A figure with its unit, in the shape the sibling tabs already use.
 *
 * 🔴 **COMPACT SINCE 2026-09-13 — owner's decision that every money figure on
 * the Management Dashboard reads `£20.00K`.** *SUPERSEDED: the full grouped
 * decimal, `formatDecimalString(amount)`.*
 *
 * ⚠️ **`compactMoneyParts` IS THE ONE FORMATTER AND NOTHING HERE MAY BUILD A
 * SECOND.** Two `Intl.NumberFormat` calls drift, and what drifts is how money
 * reads on one tab against another — the four tabs of this dashboard sit a
 * click apart, so the disagreement is visible side by side within seconds.
 *
 * ⚠️ **THE THREE PIECES RENDER BACK TO BACK WITH NO WHITESPACE**, which is why
 * this goes through `CompactFigure` rather than joining the parts here: the DOM
 * text has to stay exactly `£20.00K`, because that is what a screen reader
 * announces and what a selection copies. **Only the UNIT is gold** — not the
 * symbol, not the digits, not the separators; `src/index.css` reserves that
 * token for exactly this.
 *
 * ⛔ **`1.20M` HAS DISCARDED EVERYTHING BELOW THE HUNDRED THOUSAND, so two
 * figures 40,000 apart render identically.** The `title` is the only place the
 * precise number exists for this tile — never remove it, and never parse the
 * compacted text back or compare two of them.
 *
 * ⚠️ **THE CODE SPAN STAYS, EVEN THOUGH THE COMPACTED HEAD CARRIES A SYMBOL.**
 * This panel is genuinely multi-currency — *"No USD revenue in scope"* is a
 * reachable state one tile over — and **a symbol is not a code**: `$` is four
 * currencies. A redundant `GBP £20.00K` is visibly redundant and a bare
 * `$20.00K` is silently ambiguous, so this fails towards the visible wrong
 * answer, as everything else here does.
 */
function Money({ amount, currency }: { amount: string; currency: string }): ReactElement {
  return (
    <span
      className="flex items-baseline gap-1.5"
      title={`${formatDecimalString(amount)} ${currency}`}
    >
      {/* No code span — the symbol already carries the unit, and the panel
          header names it. See `Money` in `RevenueCompanyTable.tsx`. */}
      <span className={FIGURE}>
        <CompactFigure parts={compactMoneyParts(amount, currency)} />
      </span>
    </span>
  );
}

export function RevenueProductHeadline({
  groups,
  total,
  uncodedTotal,
  currency,
  taxLabel,
  loading,
}: {
  /**
   * The products currently on screen, **in whatever order the reader chose**.
   *
   * ⚠️ *SUPERSEDED: "ranked largest first".* That held while the only ordering
   * was revenue-descending. The table's headings are sortable now, so this
   * arrives ordered by product name or by cycles sold in just as readily —
   * **nothing here may read a figure off a POSITION.**
   */
  groups: readonly ProductGroup[];
  /**
   * The exact sum of `groups`.
   *
   * ⚠️ **PASSED IN RATHER THAN RE-SUMMED HERE, so this tile and the share column
   * in the table below are literally the same figure.** Two calls to one pure
   * function cannot disagree today; what they can do is come apart the day
   * somebody changes what "the total" is scoped to in one caller and not the
   * other — and a headline that does not equal the shares under it reads as a
   * broken page.
   */
  total: string | null;
  /**
   * The uncatalogued bucket's own total **in the current scope, whether or not
   * it is currently listed**. `null` when there is none in this currency.
   */
  uncodedTotal: string | null;
  currency: string;
  /** `incl. tax` or `ex-tax`, from the server's `meta.measure` echo. */
  taxLabel: string;
  loading: boolean;
}): ReactElement {
  /*
   * 🔴 **THE LARGEST CATALOGUED PRODUCT, NOT THE LARGEST ROW.** See the header:
   * `groups[0]` is the uncatalogued bucket on live data, which made this tile
   * print tile 4's figure under a label that calls a non-product a product.
   *
   * 🔴 **COMPUTED BY COMPARING FIGURES, NEVER BY TAKING A POSITION.** ⚠️
   * *SUPERSEDED: "`.find` on a list already ranked largest first, so this is the
   * first catalogued row and no second sort is needed — and it must stay that
   * way."* **The premise died the moment the table's headings became sortable,
   * and the sentence promising it would not is exactly what made it look safe.**
   * Measured on screen 2026-09-11 with the table ordered by *Cycles sold in*:
   * this tile read **"0% · Edumate Global - Loans Advisory: Premium Support"**
   * beside a table whose largest catalogued row was 57.1% — a plausible product
   * name against a plausible percentage, both wrong, with nothing failing.
   *
   * A `reduce` cannot be wrong about this whatever the reader has sorted by, so
   * the tile no longer depends on a property of its input that no type states
   * and nothing checks.
   *
   * ⚠️ **`Number` FOR THE COMPARISON ONLY — the figure printed is the exact
   * string**, as everywhere else on this tab.
   *
   * ⚠️ It is `ProductGroup | undefined`, and the guard is real rather than
   * ceremony: a scope holding nothing but uncatalogued revenue is reachable, and
   * so is a currency nothing was billed in. The tile has to say so rather than
   * read a label off nothing.
   */
  const largest = groups.reduce<ProductGroup | undefined>((best, group) => {
    if (group.isUncoded) return best;
    if (best === undefined) return group;
    return Number(group.total) > Number(best.total) ? group : best;
  }, undefined);
  const largestShare = largest === undefined ? null : shareOf(largest.total, total);

  /*
   * 🔴 **WHETHER THE BUCKET IS IN `total`, ASKED OF THE DATA RATHER THAN OF A
   * CONTROL.**
   *
   * ⚠️ *SUPERSEDED: an `includeUncoded` prop, passed down from the filter bar's
   * Included/Excluded toggle.* That named the ONE control that could take the
   * bucket out of `groups` — and the Product column's funnel is now a second
   * one. With the bucket unticked there, `includeUncoded` still read `true`, so
   * the tile divided the bucket's unfiltered total by a total that no longer
   * contained it: **"UNCATALOGUED 5733.7%"**, measured on screen 2026-09-11.
   *
   * Reading `groups` answers the question the arithmetic actually asks — *is the
   * numerator inside the denominator* — so it is right for every control that
   * exists and for every one added later. **A flag naming a control can only
   * ever describe the controls that existed when it was written.**
   */
  const bucketIsListed = groups.some((group) => group.isUncoded);
  const uncodedShare = bucketIsListed && uncodedTotal !== null ? shareOf(uncodedTotal, total) : null;

  return (
    <Panel className="overflow-hidden">
      <ul
        /*
         * ⚠️ The unit is in the accessible name because it is in every figure
         * below it. A screen reader reaching this list out of order otherwise
         * gets four numbers with no currency against them.
         */
        aria-label={`Revenue by product summary, ${currency}, paid, ${taxLabel}`}
        className="m-0 grid list-none grid-cols-1 gap-px bg-border p-0 sm:grid-cols-2 lg:grid-cols-4"
      >
        <Tile label="Paid revenue" loading={loading}>
          {total === null ? (
            <NoFigure note={`No ${currency} revenue in scope`} />
          ) : (
            <Money amount={total} currency={currency} />
          )}
        </Tile>

        <Tile label="Products listed" loading={loading}>
          <span className={FIGURE}>{groups.length.toLocaleString()}</span>
          {bucketIsListed && (
            <span className="mt-0.5 block text-xs text-fg-muted">
              one of them is the uncatalogued bucket
            </span>
          )}
        </Tile>

        <Tile label="Largest catalogued product" loading={loading}>
          {largest === undefined || largestShare === null ? (
            /*
             * ⚠️ **A REAL FINDING RATHER THAN AN EMPTY TILE.** A scope in which
             * every figure is uncatalogued is exactly the state this row of
             * tiles exists to expose, so it is named in words.
             */
            <NoFigure note="No catalogued product in scope" />
          ) : (
            <>
              <span className={FIGURE}>{shareText(largestShare)}</span>
              {/*
               * 🔴 **THE NAME IS HALF THE TILE.** A percentage with nothing
               * beside it is a statistic; with the product against it, it is an
               * answer. `title` carries the full string because the cell
               * truncates and a truncated product name is a different product.
               */}
              <span
                title={largest.label.text}
                className="mt-0.5 block truncate text-xs text-fg-muted"
              >
                {largest.label.text}
              </span>
            </>
          )}
        </Tile>

        <Tile label="Uncatalogued" loading={loading}>
          {uncodedTotal === null ? (
            /*
             * Nothing uncatalogued in this scope. Named as a measurement rather
             * than left as a bare dash, which reads as a tile that failed.
             */
            <NoFigure note="None in scope" />
          ) : bucketIsListed ? (
            <>
              <span className={FIGURE}>{shareText(uncodedShare)}</span>
              {/*
               * ⛔ **THIS AMOUNT IS EXACT AND IS DELIBERATELY NOT COMPACTED —
               * the 2026-09-13 compaction stops at the headline figure above
               * it.** Both sub-lines in this tile, and the matching sentence in
               * `RevenueProductNotices`, QUANTIFY A GAP: the reader is being
               * told *this much is missing from the numbers you are reading*.
               *
               * Three reasons, and the first is the one that decides it:
               *
               *   1. A gap figure is the thing a reader RECONCILES with —
               *      against Xero, against a spreadsheet, against last month's
               *      screenshot. `£1.20M` is unreconcilable by construction: it
               *      has thrown away up to £5,000 OF THE VERY QUANTITY IT IS
               *      REPORTING AS UNACCOUNTED FOR. Rounding the measure of an
               *      error is how a £4,000 discrepancy reads as agreement.
               *   2. Compaction buys SCANNING — four tiles aligning, a money
               *      column that does not wrap. This is prose read once, in
               *      full, with nothing beside it to align to, so the whole
               *      benefit is absent and only the cost lands.
               *   3. A `title` does not rescue it here. A sentence is what gets
               *      copied, pasted into an email and screenshotted, and a
               *      hover attribute survives none of those; the tile's figure
               *      above has the table underneath it as a second route to the
               *      truth, and this sentence has none.
               *
               * ⚠️ **Applied CONSISTENTLY: every gap amount on this tab is
               * exact, every scope amount is compact.** The rule is what the
               * figure is FOR, never where it renders — do not "finish" the
               * compaction by sweeping these three.
               */}
              <span className="mt-0.5 block text-xs text-fg-muted">
                {currency} {formatDecimalString(uncodedTotal)} is not attributed to a product
              </span>
            </>
          ) : (
            /*
             * ⚠️ With the bucket filtered out its share of what is on screen is
             * `0%` by construction, and `0%` here would read as "almost
             * everything is catalogued" — the opposite of the truth. The amount
             * withheld is the honest figure, and it says it is withheld.
             *
             * ⚠️ **AND IT SAYS "not listed below" WITHOUT NAMING WHICH CONTROL
             * DID IT** — the bar's toggle and the Product funnel both land here,
             * and a sentence naming one would send a reader to the wrong one.
             */
            <>
              <span className={cx(FIGURE, 'text-fg-subtle')}>Excluded</span>
              {/*
               * ⛔ **EXACT, for the reason spelled out at the sibling branch
               * above** — this is the same gap amount, reported from the state
               * where it is not on screen at all, so it is the one figure on
               * this tab with no second route to the truth anywhere on the
               * page. Compacting it is strictly worse here than in the branch
               * above, not better.
               */}
              <span className="mt-0.5 block text-xs text-fg-muted">
                {currency} {formatDecimalString(uncodedTotal)} is not listed below
              </span>
            </>
          )}
        </Tile>
      </ul>
    </Panel>
  );
}
